<template><div></div></template>

<script setup lang="ts">
import { type PropType, defineComponent, h, onMounted, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import type {
  GooglePayButtonOptions,
  GooglePayPaymentsError,
} from '../../../../types/google-pay'
import { getRedirection } from '../../helpers/getRedirection'
import type {
  PaymentFormAdapterEmits,
  PaymentFormAdapterExpose,
  PaymentFormAdapterProps,
} from '../../types/PaymentFormAdapter'
import { PaymentSetupError } from '../../types/PaymentSetupError'
import type { PaymentSubmitButtonActionType } from '../../types/PaymentSubmitButtonActionType'
import { PaymentSubmitError } from '../../types/PaymentSubmitError'

import translations from './AdyenGooglePayAdapter.translations'
import { useGooglePay } from './composables/useGooglePay'
import { normalizeGooglePayCardType } from './helpers/normalizeGooglePayCardType'

const props = defineProps<PaymentFormAdapterProps>()
const emit = defineEmits<PaymentFormAdapterEmits>()

const googlePay = useGooglePay(() => props.paymentMethod)
const marketplace = useMarketplace()
const i18n = useI18n()
const i18nLocale = useI18nLocale()
const $httpFetch = useHttpFetch()

function isGooglePayError(error: unknown): error is GooglePayPaymentsError {
  return typeof (error as GooglePayPaymentsError).statusCode === 'string'
}

function toSubmitError(error: unknown): PaymentSubmitError {
  if (!isGooglePayError(error)) {
    return PaymentSubmitError.fromAnyError(error)
  }

  // User canceled the payment with google pay (e.g. by closing the payment sheet)
  if (error.statusCode === 'CANCELED') {
    return new PaymentSubmitError({
      type: '/errors/payment/google-pay-user-canceled',
      message: error.statusMessage,
      cause: error,
    })
  }

  const KNOWN_ERROR_CODES = [
    'BUYER_ACCOUNT_ERROR',
    'DEVELOPER_ERROR',
    'MERCHANT_ACCOUNT_ERROR',
    'INTERNAL_ERROR',
  ] as const

  const knownCode = KNOWN_ERROR_CODES.find((code) => code === error.statusCode)

  const readableMessage = knownCode
    ? {
        title: i18n(translations[`${knownCode}_title`]),
        description: i18n(translations[`${knownCode}_description`]),
      }
    : {
        title: i18n(translations.defaultErrorTitle),
        description: i18n(translations.defaultErrorDescription),
      }

  return new PaymentSubmitError({
    type: '/errors/payment/unexpected/google-pay-error',
    message: error.statusMessage,
    cause: error,
    readableMessage,
  })
}

async function onGooglePaymentButtonClicked() {
  // The Google Pay button is not a form submit button, so the browser cannot
  // enforce the disabled state inherited from PaymentSubmitButton for us.
  if (props.disabled) {
    return
  }

  emit('submit-start')

  try {
    const { paymentId } = await props.createPayment()

    const paymentDataRequest = googlePay.getPaymentDataRequest({
      currencyCode: props.basePrice.currency,
      countryCode: marketplace.market.countryCode,
      totalPrice: props.basePrice.amount,
      totalPriceStatus: 'FINAL',
    })

    const paymentsClient = await googlePay.getPaymentsClient()
    const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest)

    const cardType = normalizeGooglePayCardType(
      paymentData.paymentMethodData.info?.cardFundingSource ?? '',
    )

    const response = await $httpFetch('/payment/confirm/hpp/:paymentId', {
      method: 'POST',
      pathParams: { paymentId },
      body: {
        gatewayToken: paymentData.paymentMethodData.tokenizationData.token,
        browserInfo: googlePay.collectBrowserInfo(),
        card: { type: cardType },
      },
    })

    // Redirect to payment result OR nextAction.
    const redirection = getRedirection(response.nextAction, paymentId)

    emit('submit-success', { paymentId, redirection })
  } catch (err) {
    emit('submit-error', toSubmitError(err))
  }
}

function toGooglePayButtonType(
  actionType?: PaymentSubmitButtonActionType,
): GooglePayButtonOptions['buttonType'] | undefined {
  if (actionType === 'continue') {
    return 'checkout'
  }

  return undefined
}

async function addGooglePayButton(
  container: HTMLElement,
  actionType?: PaymentSubmitButtonActionType,
) {
  const buttonLanguage = new Intl.Locale(i18nLocale).language
  const buttonType = toGooglePayButtonType(actionType)

  const buttonOptions: GooglePayButtonOptions = {
    onClick: onGooglePaymentButtonClicked,
    buttonSizeMode: 'fill',
    buttonLocale: buttonLanguage,
    ...(buttonType ? { buttonType } : {}),
  }

  const button = (await googlePay.getPaymentsClient()).createButton(
    buttonOptions,
  )

  container.appendChild(button)
}

async function prefetchGooglePaymentData() {
  const paymentDataRequest = googlePay.getPaymentDataRequest({
    currencyCode: props.basePrice.currency,
    countryCode: marketplace.market.countryCode,
    totalPrice: props.basePrice.amount,
    totalPriceStatus: 'ESTIMATED',
  })

  const paymentsClient = await googlePay.getPaymentsClient()
  paymentsClient.prefetchPaymentData(paymentDataRequest)
}

const GooglePaySubmitButton = defineComponent({
  props: {
    actionType: {
      type: String as PropType<PaymentSubmitButtonActionType>,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(buttonProps) {
    const googlePayButtonContainer = ref<HTMLElement | null>(null)

    onMounted(async () => {
      if (!googlePayButtonContainer.value) {
        return
      }

      emit('setup-start')

      try {
        await Promise.all([
          addGooglePayButton(
            googlePayButtonContainer.value,
            buttonProps.actionType,
          ),
          prefetchGooglePaymentData(),
        ])

        emit('setup-success')
      } catch (err) {
        emit('setup-error', PaymentSetupError.fromAnyError(err))
      }
    })

    return () =>
      h('div', {
        ref: googlePayButtonContainer,
        class: [
          'h-48 w-full min-w-80 md:w-auto',
          buttonProps.disabled ? 'pointer-events-none' : null,
        ],
        'aria-disabled': buttonProps.disabled,
        inert: buttonProps.disabled,
      })
  },
})

defineExpose<PaymentFormAdapterExpose>({
  submitButton: {
    is: GooglePaySubmitButton,
  },
})
</script>
